import { useMemo } from 'react';
import React from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';

interface BusinessEntry {
  updated_at: string, 
  business_id: string, 
  external_business_id: string, 
  signed_up: boolean,
  pre_approved: boolean,
  currency: string,
  maximum_amount: number,
  available_amount: number,
  expires_at: string,
  redemption_token: string,
  active_advance: boolean
}

interface BusinessTableProps {
  businessData:  BusinessEntry[] ; // Ensure this matches the type you're passing
}

const BusinessTable: React.FC<BusinessTableProps> = ({ businessData }) => {
  // Memoized columns to prevent re-creation on re-renders
  const columns = useMemo<MRT_ColumnDef<BusinessEntry>[]>(
    () => [
      {
        accessorKey: 'updated_at',
        header: 'Updated At',
        size: 100,
    },
    {
        accessorKey: 'business_id',
        header: 'Business ID',
        size: 100,
        Cell: ({ row }) => (
          <a
              href={`/business?business_id=${row.original.business_id}`}
              rel="noopener noreferrer"
          >
              {row.original.business_id}
          </a>
      ),
    },
    {
      accessorKey: 'external_business_id',
      header: 'External Business ID',
      size: 100,
      Cell: ({ row }) => (
          <a
              href={`/business?business_id=${row.original.business_id}`}
              rel="noopener noreferrer"
          >
              {row.original.external_business_id}
          </a>
      ),
  },
    {
        accessorKey: 'signed_up',
        header: 'Signed Up',
        size: 100,
        Cell: ({ cell }) => (cell.getValue() ? 'Yes' : 'No'), // Render "Yes" if true, "No" if false
    },
    {
        accessorKey: 'pre_approved',
        header: 'Pre-Approved',
        size: 100,
        Cell: ({ cell }) => (cell.getValue() ? 'Yes' : 'No'), // Render "Yes" if true, "No" if false
    },
    {
        accessorKey: 'currency',
        header: 'Currency',
        size: 100,
    },
    {
        accessorKey: 'maximum_amount',
        header: 'Maximum Amount',
        size: 100,
        Cell: ({ row }) => {
          const currency = row.original.currency || ''; // Fallback to an empty string if currency is null/undefined
          const maximumAmount = row.original.maximum_amount || 0; // Default to 0 if maximum_amount is null/undefined
          
          return `${currency.toUpperCase()} ${(maximumAmount / 100).toFixed(2)}`;
        },
        
    },
    {
        accessorKey: 'available_amount',
        header: 'Available Amount',
        size: 100,
        Cell: ({ row }) => {
          const currency = row.original.currency || ''; // Fallback to an empty string if currency is null/undefined
          const availableAmount = row.original.available_amount || 0; // Default to 0 if available_amount is null/undefined
          
          return `${currency.toUpperCase()} ${(availableAmount / 100).toFixed(2)}`;
        },
    },
    {
        accessorKey: 'expires_at',
        header: 'Expires At',
        size: 100,
    },
    {
        accessorKey: 'redemption_token',
        header: 'Redemption Token',
        size: 100,
    },
    {
        accessorKey: 'active_advance',
        header: 'Active Advance',
        size: 100,
        Cell: ({ cell }) => (cell.getValue() ? 'Yes' : 'No'), // Render "Active" if true, "Inactive" if false
    }    
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: businessData, // Matches the shape of your updated webhookData
    initialState: { columnVisibility: { headers: false, business_id:false, pre_approved:false, currency:false, redemption_token:false} }, 
  });

  return <MaterialReactTable table={table} />;
};

export default BusinessTable;
